import { Link } from "react-router-dom";
import Breadcrumbs from "../../styles/components/Breadcrumbs";
import Accordion from "../../component/template/Accordion";
import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE } from "../../config/apiBase";
import "../../styles/pages/content/_pricing.scss";

export default function Pricing() {
    const {
        loginWithRedirect,
        getAccessTokenSilently,
        isAuthenticated,
        isLoading,
    } = useAuth0();
    const [plan, setPlan] = useState(null);
    const [billingError, setBillingError] = useState("");
    const [billingLoading, setBillingLoading] = useState(false);

    const apiBase = API_BASE;

    const authedFetch = useCallback(
        async (path, options = {}) => {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
            });
            return fetch(`${apiBase}${path}`, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                },
            });
        },
        [apiBase, getAccessTokenSilently]
    );

    const fetchPlan = useCallback(async () => {
        const res = await authedFetch("/profile", { method: "GET" });
        if (!res.ok) return null;
        const data = await res.json();
        const rawPlan = String(data.plan || "").toLowerCase();
        setPlan(rawPlan);
        return rawPlan;
    }, [authedFetch]);

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            setPlan(null);
            return;
        }
        let aborted = false;
        (async () => {
            try {
                const rawPlan = await fetchPlan();
                if (!aborted && !rawPlan) setPlan(null);
            } catch {
                if (!aborted) setPlan(null);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [fetchPlan, isAuthenticated, isLoading]);

    useEffect(() => {
        if (!isAuthenticated) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get("checkout") !== "success") return;
        let attempts = 0;
        const pollPlan = async () => {
            attempts += 1;
            try {
                const rawPlan = await fetchPlan();
                if (rawPlan === "pro_ai") return;
            } catch {}
            if (attempts < 3) setTimeout(pollPlan, 2000);
        };
        pollPlan();
    }, [fetchPlan, isAuthenticated]);

    const handleScrollToCompare = (event) => {
        event.preventDefault();
        const compareSection = document.getElementById("compare-table");
        if (compareSection) {
            compareSection.scrollIntoView({ behavior: "smooth" });
        } else {
            window.location.hash = "compare-table";
        }
    };

    // todo: unquote and add real comment
    // const quotes = [
    //     {
    //         quote: "Seeing my own recordings showed exactly where I was losing clarity. That feedback loop helped quantify my impact and gave me confidence I didn't have before.",
    //         name: "Pro AI user",
    //         role: "Grad SWE",
    //     },
    // ];

    const goToSignup = () =>
        loginWithRedirect({
            authorizationParams: { screen_hint: "signup" },
        });

    const isPro = plan === "pro_ai";
    const basicCta = useMemo(() => {
        if (!isAuthenticated) return "Get started free";
        return isPro ? "Update membership" : "Active";
    }, [isAuthenticated, isPro]);

    const proCta = useMemo(() => {
        if (!isAuthenticated) return "Start Pro AI";
        return isPro ? "Manage subscription" : "Upgrade";
    }, [isAuthenticated, isPro]);

    const handleBillingAction = async () => {
        setBillingError("");
        if (!isAuthenticated) {
            goToSignup();
            return;
        }
        setBillingLoading(true);
        try {
            const endpoint = isPro
                ? "/billing/portal"
                : "/billing/checkout-session";
            const res = await authedFetch(endpoint, { method: "POST" });
            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Billing request failed.");
            }
            const data = await res.json();
            if (!data?.url) {
                throw new Error("Missing Stripe redirect URL.");
            }
            window.location.assign(data.url);
        } catch (err) {
            setBillingError(err?.message || "Failed to start billing.");
        } finally {
            setBillingLoading(false);
        }
    };

    return (
        <div className="pricing-page" id="pricing">
            <div className="pricing-page_container">
                {/* ── Hero ── */}
                <section className="hero">
                    <Breadcrumbs
                        items={[
                            { label: "Home", href: "/" },
                            { label: "Pricing", href: "/pricing" },
                        ]}
                    />
                    {/* <p className="hero-eyebrow">Simple, transparent pricing</p> */}
                    <h1>Choose a plan that fits your goals</h1>
                    <p className="hero-sub">
                        Practice like it's the real thing. Record answers,
                        review your performance, and level up with AI feedback
                        when you're ready.
                    </p>
                </section>

                {/* ── Pricing cards ── */}
                <section className="section-1">
                    <div className="grid">
                        {/* Basic */}
                        <article className="pricing-card">
                            <header className="head">
                                <h2 className="tier-name">Basic</h2>
                                <div className="pricing-container">
                                    <span className="price">£0</span>
                                    <span className="cadence">/ month</span>
                                </div>
                                <p className="tagline">
                                    Get interview-ready with the essentials.
                                </p>
                            </header>
                            <ul className="tier-list">
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    Question bank access
                                </li>
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    Record practice answers
                                </li>
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    Basic progress tracking
                                </li>
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    Save and replay recordings
                                </li>
                            </ul>
                            <div className="cta-container">
                                <button
                                    className="btn-cta"
                                    onClick={goToSignup}
                                >
                                    {basicCta}
                                </button>
                            </div>
                            <p className="fineprint">
                                No credit card required.
                            </p>
                        </article>

                        {/* Pro AI */}
                        <article className="pricing-card is-featured">
                            <div className="recommended-badge">RECOMMENDED</div>
                            <header className="head">
                                <h2 className="tier-name">Pro AI</h2>
                                <div className="pricing-container">
                                    <span className="price">£11.99</span>
                                    <span className="cadence">/ month</span>
                                </div>
                                <p className="tagline">
                                    Practice smarter with AI feedback and deeper
                                    insights.
                                </p>
                            </header>
                            <ul className="tier-list">
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    Everything in Basic
                                </li>
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    AI feedback on your recordings
                                </li>
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    Structure and clarity coaching
                                </li>
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    Actionable improvement tips
                                </li>
                                <li>
                                    <span
                                        className="pricing-icon pricing-icon--tick"
                                        aria-hidden="true"
                                    />{" "}
                                    Higher practice limits
                                </li>
                            </ul>
                            <div className="cta-container">
                                <button
                                    className="btn-cta"
                                    onClick={handleBillingAction}
                                    disabled={billingLoading}
                                >
                                    {billingLoading ? "Loading..." : proCta}
                                </button>
                            </div>
                            {billingError ? (
                                <p className="fineprint">{billingError}</p>
                            ) : null}
                            <p className="fineprint">Cancel any time.</p>
                        </article>
                    </div>

                    <a
                        href="#compare-table"
                        className="btn-compare"
                        onClick={handleScrollToCompare}
                    >
                        See all features and compare plans{" "}
                        <span
                            className="pricing-icon pricing-icon--chevrondown"
                            aria-hidden="true"
                        />
                    </a>
                </section>

                <br />
                <br />
                <br />
                <br />
                <br />

                {/* ── Testimonial ── */}
                {/* todo: unquote */}
                {/* <section className="section-2" aria-label="Testimonials">
                    <div className="testimonial__inner container">
                        <div className="testimonial__grid">
                            {quotes.map((s, i) => (
                                <figure className="quoteblock" key={i}>
                                    <blockquote className="quote">
                                        <p>"{s.quote}"</p>
                                    </blockquote>
                                    <figcaption className="meta">
                                        <span className="name">{s.name}</span>
                                        <span
                                            className="sep"
                                            aria-hidden="true"
                                        >
                                            {" "}
                                            ·{" "}
                                        </span>
                                        <span className="role">{s.role}</span>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section> */}

                {/* ── Compare table ── */}
                <section className="section-3" id="compare-table">
                    <br />
                    <br />
                    <div className="head">
                        <h2>Compare plans</h2>
                        <p className="muted">
                            Everything you need to practice, with AI when you
                            want it.
                        </p>
                    </div>

                    <div
                        className="compare-wrap"
                        role="region"
                        aria-label="Plan comparison"
                    >
                        <table className="compare-table">
                            <thead>
                                <tr>
                                    <th className="feature-col">Feature</th>
                                    <th className="plan-col plan-col--free">
                                        Basic
                                    </th>
                                    <th className="plan-col plan-col--pro">
                                        <span className="plan-title">
                                            Pro AI
                                        </span>
                                        <span className="plan-badge">
                                            Best value
                                        </span>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr className="compare-group">
                                    <th colSpan={3}>
                                        <span className="compare-group__label">
                                            Core practice
                                        </span>
                                    </th>
                                </tr>
                                <tr>
                                    <td>Question bank</td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                </tr>
                                <tr className="compare-row is-highlight">
                                    <td>Record answers and replay</td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>Practice limits</td>
                                    <td className="cell-centre text">
                                        Standard
                                    </td>
                                    <td className="cell-centre text strong">
                                        Higher
                                    </td>
                                </tr>

                                <tr className="compare-group">
                                    <th colSpan={3}>
                                        <span className="compare-group__label">
                                            Feedback &amp; insights
                                        </span>
                                    </th>
                                </tr>
                                <tr>
                                    <td>Progress tracking</td>
                                    <td className="cell-centre text">Basic</td>
                                    <td className="cell-centre text strong">
                                        Enhanced
                                    </td>
                                </tr>
                                <tr className="compare-row is-highlight">
                                    <td>AI feedback on recordings</td>
                                    <td className="cell-centre muted">—</td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>Actionable improvement tips</td>
                                    <td className="cell-centre muted">—</td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                </tr>

                                <tr className="compare-group">
                                    <th colSpan={3}>
                                        <span className="compare-group__label">
                                            Mock interviews
                                        </span>
                                    </th>
                                </tr>
                                <tr>
                                    <td>Timed mock interviews</td>
                                    <td className="cell-centre text">Basic</td>
                                    <td className="cell-centre text strong">
                                        Advanced
                                    </td>
                                </tr>
                                <tr className="compare-row is-highlight">
                                    <td>Structured review after mocks</td>
                                    <td className="cell-centre muted">—</td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                </tr>

                                <tr className="compare-group">
                                    <th colSpan={3}>
                                        <span className="compare-group__label">
                                            Security &amp; privacy
                                        </span>
                                    </th>
                                </tr>
                                <tr>
                                    <td>Private recordings by default</td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                    <td className="cell-centre">
                                        <span
                                            className="pricing-icon pricing-icon--tick"
                                            aria-hidden="true"
                                        />
                                    </td>
                                </tr>
                                <tr className="compare-row is-highlight">
                                    <td>Account and data controls</td>
                                    <td className="cell-centre text">
                                        Standard
                                    </td>
                                    <td className="cell-centre text strong">
                                        Enhanced
                                    </td>
                                </tr>
                            </tbody>

                            <tfoot>
                                <tr className="table-cta-row">
                                    <td />
                                    <td className="cell-centre">
                                        <button
                                            className="btn-table btn-table--outline"
                                            onClick={goToSignup}
                                        >
                                            Get started
                                        </button>
                                    </td>
                                    <td className="cell-centre">
                                        <button
                                            className="btn-table btn-table--primary"
                                            onClick={goToSignup}
                                        >
                                            Go Pro AI
                                        </button>
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </section>

                {/* ── FAQs ── */}
                <section className="section-4">
                    <div className="head">
                        <h2>FAQs</h2>
                        <p>Quick answers before you commit.</p>
                    </div>
                    <Accordion
                        ids={["8", "9", "10", "11", "12"]}
                        showCategories={false}
                    />
                </section>
            </div>
        </div>
    );
}
